import { LoSam } from 'src/types/losam';
import { LoSamChiTiet } from 'src/types/losamchitiet';
import { LoSamCamera, LoSamLoaiCamera } from 'src/types/camera';
import { ApiResponse } from '../types/api-response';
import { VuonTrong } from '../types/vuontrong';
import { apiGet, apiPost, apiDelete, buildQueryString } from './api-client';

// ----------------------------------------------------------------------
// VUON TRONG API SERVICE
// ----------------------------------------------------------------------

export const vuonTrongApiService = {
  /**
   * Lấy danh sách vườn trồng với phân trang và lọc
   */
  async listVuonTrong(params?: {
    skip?: number;
    take?: number;
    Status?: number;
    rowCount?: number;
    searchBy?: string[];
    orderBy?: string[];
  }): Promise<ApiResponse<VuonTrong[]>> {
    const queryString = buildQueryString(params);
    return apiGet<ApiResponse<VuonTrong[]>>(
      `/VuonTrong/ListVuonTrong${queryString}`,
      'VuonTrong',
      'Không thể tải danh sách vườn trồng'
    );
  },

  /**
   * Lấy thông tin chi tiết một vườn trồng
   */
  async getVuonTrong(id: number): Promise<ApiResponse<VuonTrong>> {
    return apiGet<ApiResponse<VuonTrong>>(
      `/VuonTrong/GetVuonTrongById?id=${id}`,
      'VuonTrong',
      'Không thể tải thông tin vườn trồng'
    );
  },

  /**
   * Tạo vườn trồng mới
   */
  async createVuonTrong(model: Partial<VuonTrong>): Promise<ApiResponse<VuonTrong>> {
    model.vuonTrong_ID = 0;
    return apiPost<ApiResponse<VuonTrong>>(
      '/VuonTrong/AddVuonTrong',
      model,
      'VuonTrong',
      false,
      'Không thể tạo vườn trồng'
    );
  },

  /**
   * Cập nhật thông tin vườn trồng
   */
  async editVuonTrong(model: Partial<VuonTrong>): Promise<ApiResponse<VuonTrong>> {
    return apiPost<ApiResponse<VuonTrong>>(
      '/VuonTrong/EditVuonTrong',
      model,
      'VuonTrong',
      false,
      'Không thể cập nhật vườn trồng'
    );
  },

  /**
   * Xóa vườn trồng (soft delete)
   */
  async deleteVuonTrong(id: number, trangThai: number = 3): Promise<ApiResponse<VuonTrong>> {
    return apiDelete<ApiResponse<VuonTrong>>(
      `/VuonTrong/DeleteVuonTrong?id=${id}&trangthai=${trangThai}`,
      'VuonTrong',
      'Không thể xóa vườn trồng'
    );
  },

  /**
   * Lấy tất cả vườn trồng (không phân trang)
   */
  async getAllVuonTrong(): Promise<ApiResponse<VuonTrong[]>> {
    return apiGet<ApiResponse<VuonTrong[]>>(
      '/VuonTrong/GetAllVuonTrong',
      'VuonTrong',
      'Không thể tải danh sách vườn trồng'
    );
  },

  /**
   * Lấy vườn trồng theo người dùng
   */
  async getVuonTrongByUser(): Promise<ApiResponse<VuonTrong[]>> {
    return apiGet<ApiResponse<VuonTrong[]>>(
      '/VuonTrong/GetVuonTrongByUser',
      'VuonTrong',
      'Không thể tải danh sách vườn trồng của bạn'
    );
  },

  /**
   * Thống kê vườn trồng
   */
  async getVuonTrongStatistics(vuonTrongId?: number): Promise<ApiResponse<any>> {
    const queryString = vuonTrongId ? `?vuonTrongId=${vuonTrongId}` : '';
    return apiGet<ApiResponse<any>>(
      `/VuonTrong/GetStatistics${queryString}`,
      'VuonTrong',
      'Không thể tải thống kê vườn trồng'
    );
  },

  /**
   * Kích hoạt/Vô hiệu hóa vườn trồng
   */
  async updateVuonTrongStatus(id: number, trangThai: number): Promise<ApiResponse<VuonTrong>> {
    return apiPost<ApiResponse<VuonTrong>>(
      '/VuonTrong/UpdateStatus',
      { id, trangThai },
      'VuonTrong',
      false,
      'Không thể cập nhật trạng thái vườn trồng'
    );
  },
};

// ----------------------------------------------------------------------
// LO SAM API SERVICE
// ----------------------------------------------------------------------

export const losamApiService = {
  /**
   * Lấy danh sách lô sâm với phân trang và lọc
   */
  async listLoSam(params?: {
    skip?: number;
    take?: number;
    Status?: number;
    rowCount?: number;
    searchBy?: string[];
    orderBy?: string[];
    vuonTrongId?: number;
  }): Promise<ApiResponse<LoSam[]>> {
    const queryString = buildQueryString(params);
    return apiGet<ApiResponse<LoSam[]>>(
      `/VuonTrong/ListLoSam${queryString}`,
      'VuonTrong',
      'Không thể tải danh sách lô sâm'
    );
  },

  /**
   * Lấy thông tin chi tiết một lô sâm
   */
  async getLoSam(loSamId: number): Promise<ApiResponse<LoSam>> {
    return apiGet<ApiResponse<LoSam>>(
      `/VuonTrong/GetLoSam/${loSamId}`,
      'VuonTrong',
      'Không thể tải thông tin lô sâm'
    );
  },

  /**
   * Thêm lô sâm mới với chi tiết và camera
   */
  async addLoSam(
    loSamData: LoSam,
    loSamChiTiets: LoSamChiTiet[],
    loSamCameras: LoSamCamera[],
    file?: File
  ): Promise<ApiResponse<LoSam>> {
    const formData = new FormData();

    const modelJson = {
      LoSam: loSamData,
      LoSamChiTiets: loSamChiTiets,
      LoSamCameras: loSamCameras
    };

    formData.append('modelJson', JSON.stringify(modelJson));

    if (file) {
      formData.append('file', file);
    }

    return apiPost<ApiResponse<LoSam>>(
      '/VuonTrong/AddLoSam',
      formData,
      'VuonTrong',
      true,
      'Không thể tạo lô sâm mới'
    );
  },

  /**
   * Cập nhật thông tin lô sâm
   */
  async editLoSam(
    loSamData: LoSam,
    loSamChiTiets: LoSamChiTiet[],
    loSamCameras: LoSamCamera[],
    file?: File
  ): Promise<ApiResponse<LoSam>> {
    const formData = new FormData();

    const modelJson = {
      LoSam: loSamData,
      LoSamChiTiets: loSamChiTiets,
      LoSamCameras: loSamCameras,
      SensorIds: loSamData.sensorIds
    };

    formData.append('modelJson', JSON.stringify(modelJson));

    if (file) {
      formData.append('file', file);
    }

    return apiPost<ApiResponse<LoSam>>(
      `/VuonTrong/EditLoSam/${loSamData.loSam_ID}`,
      formData,
      'VuonTrong',
      true,
      'Không thể cập nhật lô sâm'
    );
  },

  /**
   * Xóa lô sâm
   */
  async deleteLoSam(loSamId: number): Promise<ApiResponse<any>> {
    return apiDelete<ApiResponse<any>>(
      `/VuonTrong/DeleteLoSam?id=${loSamId}`,
      'VuonTrong',
      'Không thể xóa lô sâm'
    );
  },

  /**
   * Lấy tất cả lô sâm
   */
  async getAllLoSam(params?: {
    skip?: number;
    top?: number;
    searchby?: string;
    orderby?: string;
  }): Promise<ApiResponse<LoSam[]>> {
    const queryString = buildQueryString(params);
    return apiGet<ApiResponse<LoSam[]>>(
      `/VuonTrong/GetAllLoSam${queryString}`,
      'VuonTrong',
      'Không thể lấy danh sách lô sâm'
    );
  },

  /**
   * Lấy lô sâm theo vườn trồng
   */
  async getLoSamByVuonTrong(vuonTrongId: number): Promise<ApiResponse<LoSam[]>> {
    return apiGet<ApiResponse<LoSam[]>>(
      `/VuonTrong/GetLoSamByVuonTrong/${vuonTrongId}`,
      'VuonTrong',
      'Không thể tải danh sách lô sâm'
    );
  },

  /**
   * Thống kê lô sâm
   */
  async getLoSamStatistics(loSamId?: number): Promise<ApiResponse<any>> {
    const queryString = loSamId ? `?loSamId=${loSamId}` : '';
    return apiGet<ApiResponse<any>>(
      `/VuonTrong/GetLoSamStatistics${queryString}`,
      'VuonTrong',
      'Không thể tải thống kê lô sâm'
    );
  },

  /**
   * Cập nhật trạng thái lô sâm
   */
  async updateLoSamStatus(loSamId: number, trangThai: number): Promise<ApiResponse<LoSam>> {
    return apiPost<ApiResponse<LoSam>>(
      '/VuonTrong/UpdateLoSamStatus',
      { loSamId, trangThai },
      'VuonTrong',
      false,
      'Không thể cập nhật trạng thái lô sâm'
    );
  },
};

// ----------------------------------------------------------------------
// LO SAM CHI TIET API SERVICE
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// LOAI CAMERA API SERVICE
// ----------------------------------------------------------------------


export default {
  vuonTrongApiService,
  losamApiService,
};